import { apiCall, apiCallWithMeta } from './api';
import type {
  Game,
  GameProposal,
  GameStatus,
  RecordGameRequest,
  CreateGameProposalRequest,
  AdminUpdateGameRequest,
} from '@sulis-liiga/common';
import { API_ROUTES } from '@sulis-liiga/common';

export interface GameResultData {
  /** Games (sets) won, keyed by team id. */
  scores: Record<string, number>;
  /** Per-set point tallies; each entry maps team id → points in that set. */
  sets: Array<Record<string, number>>;
  /**
   * Present when the game ended in a retirement: the id of the team that retired.
   * The non-retiring team is recorded as the winner. May accompany partial or no
   * scores.
   */
  retiredTeamId?: string;
}

const gameService = {
  createGame: async (gameData: Partial<Game>): Promise<Game> => {
    return apiCall<Game>('post', API_ROUTES.GAME.CREATE, gameData);
  },

  // Record an already-played game together with its result in one step.
  recordGame: async (data: RecordGameRequest): Promise<Game> => {
    return apiCall<Game>('post', API_ROUTES.GAME.RECORD, data);
  },

  // Lists games from the paginated /v1/games endpoint. This is the single shared
  // source used by every games listing in the app (the Games page, the per-division
  // lists on the Divisions page, team/home widgets) — callers filter the result
  // client-side rather than each rolling its own query.
  //
  // By default it pages through EVERY result so a listing is never silently clipped
  // to the backend's default page size (e.g. a season-wide fetch that has more games
  // than one page — the tail, which includes unscheduled PENDING fixtures, would
  // otherwise be dropped). Pass an explicit `page` to fetch just that single page.
  getAllGames: async (filters?: {
    divisionId?: string;
    teamId?: string;
    status?: GameStatus;
    seasonId?: string;
    page?: number;
    limit?: number;
  }): Promise<Game[]> => {
    const buildUrl = (page?: number, limit?: number): string => {
      const params = new URLSearchParams();
      if (filters?.divisionId) params.append('divisionId', filters.divisionId);
      if (filters?.teamId) params.append('teamId', filters.teamId);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.seasonId) params.append('seasonId', filters.seasonId);
      if (page) params.append('page', page.toString());
      if (limit) params.append('limit', limit.toString());
      const qs = params.toString();
      return qs ? `${API_ROUTES.GAME.LIST}?${qs}` : API_ROUTES.GAME.LIST;
    };

    // Caller pinned a specific page — honour single-page semantics.
    if (filters?.page) {
      return apiCall<Game[]>('get', buildUrl(filters.page, filters.limit));
    }

    // Otherwise walk all pages so the full filtered set is returned.
    const limit = filters?.limit ?? 100;
    const all: Game[] = [];
    let page = 1;
    // Hard cap as a runaway guard; real listings are far smaller.
    for (let guard = 0; guard < 1000; guard++) {
      const { data, meta } = await apiCallWithMeta<Game[]>('get', buildUrl(page, limit));
      if (Array.isArray(data)) all.push(...data);
      const totalPages = meta?.pages ?? 1;
      if (page >= totalPages) break;
      page += 1;
    }
    return all;
  },

  getGameById: async (gameId: string): Promise<Game> => {
    return apiCall<Game>('get', API_ROUTES.GAME.DETAILS(gameId));
  },

  updateGame: async (gameId: string, gameData: Partial<Game>): Promise<Game> => {
    return apiCall<Game>('patch', API_ROUTES.GAME.UPDATE(gameId), gameData);
  },

  cancelGame: async (gameId: string, reason?: 'cancelled' | 'abandoned'): Promise<Game> => {
    if (reason) {
      return apiCall<Game>('post', API_ROUTES.GAME.CANCEL(gameId), { reason });
    }
    return apiCall<Game>('post', API_ROUTES.GAME.CANCEL(gameId));
  },

  adminUpdateGame: async (gameId: string, data: AdminUpdateGameRequest): Promise<Game> => {
    return apiCall<Game>('put', API_ROUTES.GAME.ADMIN_UPDATE(gameId), data);
  },

  submitGameResult: async (gameId: string, resultData: GameResultData): Promise<Game> => {
    return apiCall<Game>('post', API_ROUTES.GAME.SUBMIT_RESULT(gameId), resultData);
  },

  approveResult: async (gameId: string): Promise<Game> => {
    return apiCall<Game>('post', API_ROUTES.GAME.APPROVE_RESULT(gameId));
  },

  // Admin-only: approve a pending result on behalf of the players (finalizes it).
  adminApproveResultOnBehalf: async (gameId: string): Promise<Game> => {
    return apiCall<Game>('post', API_ROUTES.GAME.APPROVE_RESULT_ON_BEHALF(gameId));
  },

  // Admin-only: reset a completed game's result back to a pending fixture (reverses
  // the standings the result awarded).
  resetGameResult: async (gameId: string): Promise<Game> => {
    return apiCall<Game>('post', API_ROUTES.GAME.RESET_RESULT(gameId));
  },

  getResultStatus: async (
    gameId: string,
  ): Promise<{
    verified: boolean;
    verifications: Array<{ userId: string; approved: boolean; timestamp: string }>;
  }> => {
    return apiCall('get', API_ROUTES.GAME.RESULT_STATUS(gameId));
  },

  disputeGameResult: async (gameId: string, reason: string): Promise<Game> => {
    return apiCall<Game>('post', API_ROUTES.GAME.DISPUTE(gameId), { reason });
  },

  resolveDispute: async (
    gameId: string,
    decision: {
      decision: string;
      updatedResult?: GameResultData;
      notes: string;
    },
  ): Promise<Game> => {
    return apiCall<Game>('post', API_ROUTES.GAME.RESOLVE(gameId), decision);
  },

  // Proposal methods
  getProposals: async (teamId?: string): Promise<GameProposal[]> => {
    let url = API_ROUTES.GAME.PROPOSALS;
    if (teamId) url = `${url}?teamId=${teamId}`;
    return apiCall<GameProposal[]>('get', url);
  },

  createProposal: async (data: CreateGameProposalRequest): Promise<GameProposal> => {
    return apiCall<GameProposal>('post', API_ROUTES.GAME.PROPOSALS, data);
  },

  // The PENDING fixtures the current player can still propose or record.
  getAvailableFixtures: async (): Promise<Game[]> => {
    return apiCall<Game[]>('get', API_ROUTES.GAME.FIXTURES_AVAILABLE);
  },

  acceptProposal: async (proposalId: string, slotIndex: number): Promise<GameProposal> => {
    return apiCall<GameProposal>('post', API_ROUTES.GAME.PROPOSAL_ACCEPT(proposalId), {
      slotIndex,
    });
  },

  declineProposal: async (proposalId: string): Promise<GameProposal> => {
    return apiCall<GameProposal>('post', API_ROUTES.GAME.PROPOSAL_DECLINE(proposalId));
  },

  counterProposal: async (
    proposalId: string,
    slots: Array<{ date: string; time: string; venue: { name: string; address: string } }>,
  ): Promise<GameProposal> => {
    return apiCall<GameProposal>('post', API_ROUTES.GAME.PROPOSAL_COUNTER(proposalId), { slots });
  },

  getUpcomingGames: async (): Promise<Game[]> => {
    return apiCall<Game[]>('get', `${API_ROUTES.GAME.LIST}?status=scheduled`);
  },

  getTeamGames: async (teamId: string): Promise<Game[]> => {
    return apiCall<Game[]>('get', `${API_ROUTES.GAME.LIST}?teamId=${teamId}`);
  },

  getDivisionGames: async (divisionId: string): Promise<Game[]> => {
    return apiCall<Game[]>('get', `${API_ROUTES.GAME.LIST}?divisionId=${divisionId}`);
  },
};

export default gameService;
